/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import classnames from 'classnames';
import React from 'react';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';
import Checkbox from '@react/react-spectrum/Checkbox';
import Heading from '@react/react-spectrum/Heading';
import { Tab, TabList } from '@react/react-spectrum/TabList';
import RemoveCircle from '@react/react-spectrum/Icon/RemoveCircle';
import AddCircle from '@react/react-spectrum/Icon/AddCircle';
import SideRailList from '../common/plugin.view.nav.list';
import styles from './styles/manage.view.plugins.styl';

const MSG = defineMessages({
  orgConfig: {
    id: 'gemini.settings.pluginview.orgtab',
    defaultMessage: 'For Your Company'
  },
  userConfig: {
    id: 'gemini.settings.pluginview.usertab',
    defaultMessage: 'For You'
  },
  enabledHead: {
    id: 'gemini.settings.pluginview.enabledHead',
    defaultMessage: 'Enabled'
  },
  availableHead: {
    id: 'gemini.settings.pluginview.availableHead',
    defaultMessage: 'Available'
  },
  useOrg: {
    id: 'gemini.settings.pluginview.useorg',
    defaultMessage: 'Use Org Level Defaults'
  }
});

const modes = ['org', 'userorg'];
const getPluginNamespaces = R.pluck('namespace');

const ManageViewPlugins = ({
  availableViewPlugins = [],
  izzy,
  mode = 'org',
  onMode,
  onUpdatePluginConfig,
  onToggleUserUsesOrg,
  viewPlugins
}) => {
  const allPlugins = R.flatten(viewPlugins);

  return (
    <div
      className={classnames(
        styles.root,
        (!viewPlugins || !viewPlugins.length) && styles.noViewPlugins
      )}
    >
      <TabList
        selectedIndex={modes.indexOf(mode)}
        onChange={i => onMode(modes[i])}
        quiet
      >
        <Tab>
          {izzy(MSG.orgConfig)}
        </Tab>
        <Tab>
          {izzy(MSG.userConfig)}
        </Tab>
      </TabList>
      {mode === 'userorg' && (
        <Checkbox
          className={styles.useOrg}
          label={izzy(MSG.useOrg)}
          checked={!viewPlugins || !viewPlugins.length}
          onChange={onToggleUserUsesOrg}
        />
      )}
      {(viewPlugins && viewPlugins.length) ? (
        <div className={styles.content}>
          <div className={styles.contentChild}>
            <Heading variant="subtitle2">{izzy(MSG.enabledHead)}</Heading>
            <div className={styles.listBox}>
              <SideRailList
                plugins={viewPlugins}
                itemPropsFunc={() => ({
                  toggleIcon: viewPlugins.length > 1 ? (
                    <RemoveCircle
                      className={styles.redIcon}
                      style={{ color: '#bb121a' }}
                      size="S"
                    />
                  ) : null
                })}
                onSelectView={(namespace) => {
                  if (viewPlugins.length > 1) {
                    onUpdatePluginConfig(R.without([namespace], getPluginNamespaces(allPlugins)));
                  }
                }}
              />
            </div>
          </div>
          <div className={styles.contentChild}>
            <Heading variant="subtitle2">{izzy(MSG.availableHead)}</Heading>
            <div className={styles.listBox}>
              {availableViewPlugins.length > 0 && (
                <SideRailList
                  plugins={availableViewPlugins}
                  itemPropsFunc={() => ({
                    toggleIcon: (
                      <AddCircle
                        size="S"
                      />
                    )
                  })}
                  onSelectView={namespace =>
                    onUpdatePluginConfig(R.append(namespace, getPluginNamespaces(allPlugins)))
                  }
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

ManageViewPlugins.displayName = 'ManageViewPlugins';
export default injectIntl(ManageViewPlugins);
